<template>
  <div class="tms-dashboard">
    <div class="content-body">
      <div class="nav-bar-container">
        <div class="nav-bar clearfix">
          <div class="welcome">
            <span class="notice-header">{{ $t('userGuideForms.title') }}</span>
          </div>
          <div class="summary-items" />
        </div>
      </div>

      <TabsContainer
        :tabNames="tabNames"
        class="tabs-container-wrapper user-guide-and-form-display-panel"
      >
        <div slot="tab-0">

          <Collapse
            v-for="(files, groupName) in guideGroups"
            class="guide-forms-display-collapse"
            :expand="true"
            :key="groupName"
          >
            <div
              slot="title"
              class="guide-forms-display-collapse-title"
            >{{ groupTypesName[groupName] }}</div>
            <div slot="content">
              <CommonTable
                class="guide-forms-display"
                :tableContentLists="files"
                :renderHeaderList="tableHeader"
                :interactionFunc="interactionFunc"
                defaultOrderKey="order"
                :defaultOrder="true"
                :needFilter="false"
                :tableId="'guide-forms-display-' + groupName"
              />
            </div>
          </Collapse>
        </div>

        <div slot="tab-1">
          <Collapse
            v-for="(files, groupName) in formsGroups"
            class="guide-forms-display-collapse"
            :expand="true"
            :key="groupName"
          >
            <div
              slot="title"
              class="guide-forms-display-collapse-title"
            >{{ groupTypesName[groupName] }}</div>
            <div slot="content">
              <CommonTable
                class="guide-forms-display"
                :tableContentLists="files"
                :renderHeaderList="tableHeader"
                :interactionFunc="interactionFunc"
                defaultOrderKey="order"
                :defaultOrder="true"
                :needFilter="false"
                :tableId="'guide-forms-display-' + groupName"
              />
            </div>
          </Collapse>
        </div>
      </TabsContainer>

    </div>
  </div>
</template>

<script>
import TabsContainer from '@/controls/TabsContainer'
import CommonTable from '@/controls/CommonTable'
import Collapse from '@/controls/Collapse'
import CollapseTitleSlot from '@/modules/workPermits/CollapseTitleSlot'
import sticky from '@/controls/Sticky'
import { mapGetters } from 'vuex'
import { getAttachments } from '@/api/docs/request'
import lodash from 'lodash'
import * as xfs from '@/utils/xfs'
import { DOCS_SOURCE_TYPE, regionNames } from '@/utils/consts'
import { downloadAllFilesAsZip } from '@/api/events/request'

export default {
  name: 'userGuideAndFormsDisplay',
  components: {
    TabsContainer, sticky, CommonTable, Collapse, CollapseTitleSlot,
  },
  data () {
    const guideGroupTypes = []
    const formsGroupTypes = ['tenantESXQ', 'tenantNonESXQ', 'hklVendorESXQ', 'hklVendorNonESXQ', 'hklUser']
    return {
      guideFiles: [],
      formsFiles: [],
      guideGroupTypes,
      formsGroupTypes,
      interactionFunc: {
        filename: file => this.downloadFile(file.id),
      },
      DOCS_SOURCE_TYPE,
    }
  },
  computed: {
    ...mapGetters('user', ['currentUser', 'currentUserContact', 'isEngineer', 'isContractor', 'isTenant']),
    ...mapGetters('regions', ['currentRegion']),
    guideGroups () {
      return this.filesGroupBy(this.guideFiles, this.guideGroupTypes)
    },
    formsGroups () {
      return this.filesGroupBy(this.formsFiles, this.formsGroupTypes)
    },
    tabNames () {
      return [this.$i18n.t('settings.userGuideAndForms.userGuide'), this.$i18n.t('settings.userGuideAndForms.forms')]
    },
    groupTypesName () {
      return {
        tenant: this.$i18n.t('userGuideForms.tenant'),
        contractor: this.$i18n.t('userGuideForms.contractor'),
        hklVendor: this.$i18n.t('userGuideForms.hklVendor'),
        hklUser: this.$i18n.t('userGuideForms.hklUser'),
        tenantESXQ: `${this.$i18n.t('userGuideForms.ttc')} ${this.$i18n.t('userGuideForms.exsq')}`,
        tenantNonESXQ: `${this.$i18n.t('userGuideForms.ttc')} ${this.$i18n.t('userGuideForms.nonExsq')}`,
        hklVendorESXQ: `${this.$i18n.t('userGuideForms.hklVendor')} ${this.$i18n.t('userGuideForms.exsq')}`,
        hklVendorNonESXQ: `${this.$i18n.t('userGuideForms.hklVendor')} ${this.$i18n.t('userGuideForms.nonExsq')}`,
      }
    },
    tableHeader () {
      return [
        {
          title: '#',
          name: 'order',
        },
        {
          title: this.$i18n.t('tender.attachment.document'),
          name: 'filename',
        },
        {
          title: this.$i18n.t('tender.attachment.description'),
          name: 'description',
        },
      ]
    },
  },
  methods: {
    async loadFiles () {
      this.guideFiles = await this.getAttachments(this.DOCS_SOURCE_TYPE.USER_GUIDE, true)
      this.formsFiles = await this.getAttachments(this.DOCS_SOURCE_TYPE.FORMS, true)
    },
    async getAttachments (...args) {
      const attachments = await getAttachments(...args)
      return attachments.map(attachment => {
        attachment.description = attachment.description || ''
        return attachment
      })
    },
    getFilesByGroup (files, groupName) {
      return files.filter(file => file.metaData[groupName] !== '0')
    },
    filesGroupBy (files, groups) {
      const res = {}
      groups.forEach(groupName => {
        const fileGroup = this.getFilesByGroup(files, groupName)
        if (fileGroup.length) {
          const fileGroupCopy = lodash.orderBy(lodash.cloneDeep(fileGroup), [file => file.order], ['asc'])
          fileGroupCopy.forEach((file, index) => {
            file.order = index + 1
            file.allowLink = true
          })
          res[groupName] = fileGroupCopy
        }
      })
      return res
    },
    async downloadFile (fileId) {
      await xfs.downloadFile(`/docs/${fileId}`)
    },
    async downloadAllFiles (files, zipName) {
      const formFiles = files.map(file => {
        return {
          name: file.filename,
          url: `/docs/${file.id}`,
          checked: true,
        }
      })
      await downloadAllFilesAsZip(formFiles, zipName, this.$i18n.t('action.downloadFailedPart'))
    },
    initGroupType () {
      // hkl user
      if (this.isEngineer) {
        this.guideGroupTypes = ['hklUser']
        this.formsGroupTypes = ['hklUser']
      }
      // vendor
      if (this.isContractor) {
        this.guideGroupTypes = ['hklVendor']
        this.formsGroupTypes = this.currentRegion.name === regionNames.HK ? ['hklVendorNonESXQ', 'hklVendorESXQ'] : ['hklVendorNonESXQ']
      }
      // tenant / tenant contractor
      if (this.isTenant) {
        this.formsGroupTypes = this.currentRegion.name === regionNames.HK ? ['tenantNonESXQ', 'tenantESXQ'] : ['tenantNonESXQ']
        const perms = {}
        Object.assign(perms, ...this.currentUser.roles.map(role => role.perms))
        if (perms.TenantTenancyRecord || perms.TenantProject) {
          this.guideGroupTypes.push('tenant')
        }
        if (perms.TMSProjectContractor || perms.TMSStandaloneContractor) {
          this.guideGroupTypes.push('contractor')
        }
      }
    },
  },
  async mounted () {
    if (this.currentRegion.name !== regionNames.HK) {
      this.groupTypesName.tenantNonESXQ = `${this.$i18n.t('userGuideForms.ttc')}`
      this.groupTypesName.hklVendorNonESXQ = `${this.$i18n.t('userGuideForms.hklVendor')}`
    }
    this.initGroupType()
    await this.loadFiles()
  },
}
</script>

<style lang="scss" scoped>
  @import '~styles/dashboard/tmsdashboard';

  @import '~styles/project/style';
  @import '~styles/popup';
  .tms-dashboard {
    & ::v-deep .nav-bar {
      max-width: none
    }
  }

  .guide-forms-display {
    & ::v-deep .common-table-header-container {
      min-width: 100% !important;
      max-width: 100% !important;
    }
    & ::v-deep .common-table {
      min-width: 100% !important;
      max-width: 100% !important;
      min-height: unset;
      margin: 0;
    }

    & ::v-deep .table-list {
      width: 100%;
    }

    & ::v-deep .order {
      width: 70px;
    }

    & ::v-deep .filename {
      width: 30%;
    }

    & ::v-deep .description {
      width: 50%;
    }
  }
  .tabs-container-wrapper {
    & ::v-deep .tabs-panel {
      background-color: #fefefe;
    }
    & ::v-deep .hkld-tabs {
      padding: 15px 24px;
    }
  }
</style>

<style scoped>
  .tabs-container-wrapper {
    max-width: 1440px;
    margin: 0 auto;
  }

  .guide-forms-display-collapse-title {
    font-size: 22px;
  }

  .guide-forms-display {
    margin-bottom: 15px;
  }

  .download-all-button {
    padding-right: 20px;
    padding-bottom: 15px;
    text-align: right;
  }

  .notice-header {
    font-family: Georgia;
    font-weight: bold;
    font-size: 22px;
  }

  .tabs-container-wrapper {
    max-width: none
  }

  .user-guide-and-form-display-panel {
    width: auto;
    padding: 15px 0;
    box-shadow: 0 0 4px 0 rgba(172, 172, 172, 0.5);
    background-color: #ffffff;
    margin: 15px;
    align-items: center;
  }

</style>
